import {
  Attachment,
  Command,
  File,
  User,
  UserRole,
  isPhoneNumberValid,
} from '@nextforce/api'
import { t } from 'app/i18l'
import { linking } from 'app/linking'
import * as FileSystem from 'expo-file-system/legacy'
import * as Linking from 'expo-linking'
import * as Sharing from 'expo-sharing'
import * as SMS from 'expo-sms'
import { resolveMime } from 'friendly-mimes'
import { HomeStackParamList } from 'home/stack/types'
import { Platform } from 'react-native'
import uuid from 'react-native-uuid'
import * as RNURL from 'url'
import { WebScreenParams } from 'web/types'
import { errorAlert } from './alerts'
import heic2any from './wrappers/heic2any'

export function generateUniqueId(): string {
  return uuid.v4()
}

// Strips lone UTF-16 surrogates, private-use-area "tofu box" codepoints, and
// Unicode "specials" (e.g. U+FFFC object replacement char left behind by inline
// image/attachment paste) that crash Android's text layout
// (BoringLayout "Layout: -2 < 0") when measured.
export function stripInvalidCodepoints(text: string): string {
  return Array.from(text)
    .filter(char => {
      const codePoint = char.codePointAt(0) ?? 0
      const isLoneSurrogate = codePoint >= 0xd800 && codePoint <= 0xdfff
      const isPrivateUse =
        (codePoint >= 0xe000 && codePoint <= 0xf8ff) ||
        (codePoint >= 0xf0000 && codePoint <= 0xffffd) ||
        (codePoint >= 0x100000 && codePoint <= 0x10fffd)
      const isSpecials = codePoint >= 0xfff0 && codePoint <= 0xffff
      return !isLoneSurrogate && !isPrivateUse && !isSpecials
    })
    .join('')
}

export function openMap(address: string, storename: string) {
  const url = Platform.select({
    ios: `http://maps.apple.com/?q=${storename}&address=${address}`,
    default: `https://www.google.com/maps/search/?api=1&query=${address}`,
  })

  return Linking.openURL(url)
}

async function checkNavigationAccess(navigationState: any, user: User) {
  // Extract the topmost route name
  const topRoute: keyof HomeStackParamList =
    navigationState.routes[navigationState.routes.length - 1].name

  if (topRoute === 'schedule' || topRoute === 'open_shifts') {
    if (user.permissions) {
      const { canSeeHelperSchedule, canSeeNextShifts, canSeeStoreSchedule } =
        user.permissions
      if (canSeeStoreSchedule || canSeeHelperSchedule || canSeeNextShifts) {
        return true
      }
    }
    return false
  }
  return true
}

export async function openLink(url: string, navigation: any, user: User) {
  if (linking.getStateFromPath) {
    const parsedURL = Linking.parse(url)
    if (parsedURL.path) {
      const path = RNURL.format({
        pathname: parsedURL.path,
        query: parsedURL.queryParams as any,
      })

      const navigationState = linking.getStateFromPath(path, linking.config)
      if (navigationState) {
        const accessAllowed = await checkNavigationAccess(navigationState, user)
        if (accessAllowed) {
          navigation.reset(navigationState)
        } else {
          console.warn('Access denied for route: ', path)
          errorAlert(
            new Error(t('navigationAccessDeniedMessage')),
            t('accessDenied')
          )
        }
        return
      }
    }
  }
  let link = url
  try {
    const WWW_URL_PATTERN = /^www\./i
    if (WWW_URL_PATTERN.test(url)) {
      link = `https://${url}`
    }
    await Linking.openURL(link)
  } catch (err) {
    console.warn('Error going to route ', link, ': ', err)
  }
}

export function handleWebLink(navigation: any, webParams: WebScreenParams) {
  if (Platform.OS === 'web') {
    return Linking.openURL(webParams.url)
  }
  navigation.navigate('web', webParams)
}

export async function onSendSMS(users: User[], message: string) {
  const isAvailable = await SMS.isAvailableAsync()
  const phoneNumbers = users.flatMap(user => user.phoneNumber ?? [])
  if (isAvailable) {
    const { result } = await SMS.sendSMSAsync(phoneNumbers, message)
    return result
  } else {
    // not supported
  }
}

export async function onCallSMS(command: Command) {
  if (command.type === 'call') {
    await Linking.openURL(`tel:${command.phoneNumber}`)
  } else if (command.type === 'sms') {
    const seperator = Platform.OS === 'ios' ? '&' : '?'
    await Linking.openURL(
      `sms:${command.phoneNumber}${seperator}body=${command.body ?? ' '}`
    )
  }
}

export function normalizePhoneNumber(phoneNumber: string) {
  if (!isPhoneNumberValid(phoneNumber)) {
    throw new Error(t('invalidPhoneNumber'))
  }
  return phoneNumber.length === 10 ? `1${phoneNumber}` : phoneNumber
}

export function getDarkShadeColor(opacity = 0.2) {
  return `rgba(0,0,0,${opacity})`
}
export function getLightShadeColor(opacity = 0.7) {
  return `rgba(255,255,255,${opacity})`
}

export function getPrimaryRole(
  roles: UserRole[] | undefined
): UserRole | undefined {
  if (roles?.includes('admin')) {
    return 'admin'
  } else if (roles?.includes('observer')) {
    return 'observer'
  } else if (roles?.includes('staff')) {
    return 'staff'
  } else if (roles?.includes('guest')) {
    return 'guest'
  } else if (roles?.includes('manager')) {
    return 'manager'
  } else if (roles?.includes('helper')) {
    return 'helper'
  }
}

export function convertFileToAttachment(file: File | undefined): Attachment {
  return {
    name: file?.name,
    mimeType: file?.currentVersion?.fileMimeType ?? '',
    uri: file?.currentVersion?.url ?? '',
  }
}

export async function shareAttachment(attachment: Attachment) {
  const isSharingAvailable = await Sharing.isAvailableAsync()
  if (isSharingAvailable) {
    try {
      let localUri = attachment.uri

      // Helper function to get proper filename with extension
      const getProperFilename = (originalName?: string, uri?: string) => {
        const fileExtension = resolveMime(attachment.mimeType).fileType
        let filename = originalName || uri?.split('/').pop() || 'file'

        // Check if filename already has the correct extension
        const currentExtension = filename.split('.').pop()?.toLowerCase()
        const expectedExtension = fileExtension.slice(1).toLowerCase() // remove the dot

        // Only add extension if:
        // 1. File has no extension, or
        // 2. Current extension doesn't match the mime type
        if (!currentExtension || currentExtension !== expectedExtension) {
          filename = `${filename}${fileExtension}`
        }
        return filename
      }

      // Skip file caching on web platform
      if (Platform.OS === 'web') {
        localUri = attachment.uri
      }
      // Handle remote URLs that need downloading
      else if (attachment.uri.startsWith('http')) {
        const filename = getProperFilename(attachment.name, attachment.uri)
        const downloadDir = `${FileSystem.cacheDirectory}${filename}`

        // Check if file already exists in cache
        const fileInfo = await FileSystem.getInfoAsync(downloadDir)
        if (fileInfo.exists) {
          localUri = downloadDir
        } else {
          const { uri: downloadedUri } = await FileSystem.downloadAsync(
            attachment.uri,
            downloadDir
          )
          localUri = downloadedUri
        }
      }
      // Handle local files (file:// or content://)
      else {
        const filename = getProperFilename(attachment.name, attachment.uri)
        const newPath = `${FileSystem.cacheDirectory}${filename}`

        // Check if already in cache
        const fileInfo = await FileSystem.getInfoAsync(newPath)
        if (fileInfo.exists) {
          localUri = newPath
        } else {
          // Copy the file to new location with proper name
          await FileSystem.copyAsync({
            from: attachment.uri,
            to: newPath,
          })
          localUri = newPath
        }
      }

      await Sharing.shareAsync(localUri, {
        mimeType: attachment.mimeType,
        dialogTitle: 'Sharing File',
      })
    } catch (error) {
      console.error('Error sharing attachment:', error)
    }
  }
}

/**
 * Toggles an item in an array - adds it if not present or removes it if already present
 * @param array The original array or undefined
 * @param item The item to toggle
 * @returns A new array with the item toggled
 */
export function toggleItemInArray<T>(array: T[] | undefined, item: T): T[] {
  const updatedArray = [...(array || [])]

  if (updatedArray.includes(item)) {
    // Remove the item if it exists
    updatedArray.splice(updatedArray.indexOf(item), 1)
  } else {
    // Add the item if it doesn't exist
    updatedArray.push(item)
  }

  return updatedArray
}

/**
 * Toggles an item in an object map - adds it if not present or removes it if already present
 * @param objectMap The original object map or undefined
 * @param key The key to toggle
 * @param value The value to associate with the key (only used when adding)
 * @returns A new object map with the item toggled
 */
export function toggleItemInObjectMap<K extends string | number | symbol, T>(
  objectMap: Record<K, T> | undefined,
  key: K,
  value: T
): Record<K, T> {
  // Create a properly typed empty object if none exists
  const updatedMap = objectMap ? { ...objectMap } : ({} as Record<K, T>)

  if (key in updatedMap) {
    // Remove the item if it exists
    delete updatedMap[key]
  } else {
    // Add the item if it doesn't exist
    updatedMap[key] = value
  }

  return updatedMap
}

export async function convertHeicToJpegWeb(
  uri: string,
  mimeType: string
): Promise<{ uri: string; mimeType: string } | null> {
  try {
    let blob: Blob
    if (uri.startsWith('data:')) {
      // Convert base64 data URL to Blob
      const base64Data = uri.split(',')[1]
      const byteCharacters = atob(base64Data)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      blob = new Blob([byteArray], { type: mimeType })
    } else {
      // blob: or http(s): URLs (e.g. from the document picker on web)
      const response = await fetch(uri)
      if (!response.ok) {
        throw new Error(
          `Failed to fetch image for HEIC conversion: ${response.status} ${response.statusText}`
        )
      }
      blob = await response.blob()
    }
    const convertedBlob = await heic2any({
      blob,
      toType: 'image/jpeg',
      quality: 1,
    })
    const blobToUse = Array.isArray(convertedBlob)
      ? convertedBlob[0]
      : convertedBlob
    const jpegUri = URL.createObjectURL(blobToUse)
    return { uri: jpegUri, mimeType: 'image/jpeg' }
  } catch (e) {
    console.error('[ImagePicker] Web HEIC/HEIF conversion failed:', e)
    return null
  }
}
